import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { protectedRoutes } from '@/routes/config';

type BreadcrumbEntry = {
  matcher: RegExp;
  crumbs: readonly string[];
  rootPath: string;
  parentPattern: string | null;
};

export type BreadcrumbResult = {
  crumbs: readonly string[];
  pageCrumb: string;
  rootPath: string;
  parentPath: string | null;
};

const IGNORE_SEGMENTS = new Set(['edit', 'create', 'detail', 'details']);

// Route segment -> translation key, where the two differ (e.g. the sidebar's
// existing `nav.family_details` label already covers the `family` route).
const CUSTOM_LABELS: Record<string, string> = {
  family: 'family_details',
  education: 'education_details',
  banking: 'banking_details',
  assets: 'asset_details',
  surveys: 'survey',
  trainings: 'my_trainings',
};

const INDEX_CRUMB_OVERRIDES: Record<string, string> = {
  'leave-management': 'overview',
};

// Full route path -> crumbs (and the middle crumb's target), where the route
// segments do not read well. My team's pages read as their tab labels
// (`nav.my_team_tabs.*`, the same words as the tabs), and a member's page
// goes back to the People tab: there is no `/my-team/members` page.
const PATH_OVERRIDES: Record<
  string,
  { crumbs: string[]; parentPattern?: string }
> = {
  'my-team/team': { crumbs: ['my_team', 'my_team_tabs.team'] },
  'my-team/calendar': { crumbs: ['my_team', 'my_team_tabs.calendar'] },
  'my-team/trainings': { crumbs: ['my_team', 'my_team_tabs.trainings'] },
  'my-team/approvals': { crumbs: ['my_team', 'my_team_tabs.approvals'] },
  'my-team/delegations': { crumbs: ['my_team', 'my_team_tabs.delegations'] },
  'my-team/members/:employeeId': {
    crumbs: ['my_team', 'my_team_tabs.people', 'detail'],
    parentPattern: 'my-team',
  },
};

const formatSegment = (seg: string): string =>
  seg.replace(/:/g, '').replace(/-/g, '_');

function crumbFor(seg: string): string {
  if (seg.startsWith(':')) return 'detail';
  return CUSTOM_LABELS[seg] ?? formatSegment(seg);
}

function buildBreadcrumbData() {
  const staticMap = new Map<string, BreadcrumbEntry>();
  const dynamicEntries: BreadcrumbEntry[] = [];

  const register = (
    fullPath: string,
    crumbs: string[],
    rootSegment: string
  ) => {
    const matcher = new RegExp(`^/${fullPath.replace(/:[^/]+/g, '[^/]+')}$`);
    const pathParts = fullPath.split('/');
    const override = PATH_OVERRIDES[fullPath];
    const parentPattern =
      override?.parentPattern ??
      (pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : null);

    const entry: BreadcrumbEntry = {
      matcher,
      crumbs: Object.freeze(override?.crumbs ?? crumbs),
      rootPath: `/${rootSegment}`,
      parentPattern,
    };

    if (fullPath.includes(':')) {
      dynamicEntries.push(entry);
    } else {
      staticMap.set(`/${fullPath}`, entry);
    }
  };

  const crumbsFor = (segments: string[]) =>
    segments
      .filter((seg) => seg.startsWith(':') || !IGNORE_SEGMENTS.has(seg))
      .map(crumbFor);

  for (const route of protectedRoutes) {
    // Some routes (surveys) are registered with a leading slash and nested
    // segments (`/surveys/:id`) — the root segment is always the first one.
    const routeSegments = route.path.split('/').filter(Boolean);
    const routePath = routeSegments.join('/');
    const [rootSegment] = routeSegments;
    const indexCrumb = INDEX_CRUMB_OVERRIDES[routePath];
    register(
      routePath,
      indexCrumb
        ? [crumbFor(rootSegment), crumbFor(indexCrumb)]
        : [crumbFor(rootSegment), ...crumbsFor(routeSegments.slice(1))],
      rootSegment
    );

    for (const child of route.children ?? []) {
      if (child.index || !child.path) continue;

      const fullPath = `${routePath}/${child.path}`;
      register(
        fullPath,
        [crumbFor(rootSegment), ...crumbsFor(fullPath.split('/').slice(1))],
        rootSegment
      );
    }
  }

  // Sort by literal segment count: more literals = more specific, so
  // `files/:folderId/:subFolderId` is checked before `files/:folderId`.
  dynamicEntries.sort((a, b) => {
    const literalCount = (e: BreadcrumbEntry) =>
      e.matcher.source.split('/').filter((s) => s && s !== '[^/]+').length;
    return literalCount(b) - literalCount(a);
  });

  return { staticMap, dynamicEntries };
}

// Built once at module load — O(1) static lookup, O(k) dynamic scan where k << n
const { staticMap, dynamicEntries } = buildBreadcrumbData();

function resolveParentPath(parentPattern: string, pathname: string): string {
  const pathSegments = pathname.split('/').filter(Boolean);
  const resolved = parentPattern
    .split('/')
    .map((seg, i) => (seg.startsWith(':') ? (pathSegments[i] ?? seg) : seg))
    .join('/');
  return `/${resolved}`;
}

export function useBreadcrumbs(): BreadcrumbResult | null {
  const { pathname } = useLocation();

  return useMemo(() => {
    // The dashboard keeps the rotating welcome message instead of a title.
    if (pathname === '/dashboard') return null;

    const entry =
      staticMap.get(pathname) ??
      dynamicEntries.find((e) => e.matcher.test(pathname));

    if (!entry) return null;

    const parentPath = entry.parentPattern
      ? resolveParentPath(entry.parentPattern, pathname)
      : null;

    return {
      crumbs: entry.crumbs,
      pageCrumb: entry.crumbs[0],
      rootPath: entry.rootPath,
      parentPath,
    };
  }, [pathname]);
}
